import React, { useState, useEffect } from 'react';
import { Form } from 'react-bootstrap';

interface Option {
    value: string;
    label: string;
}

interface SearchableDropdownProps {
    options: Option[];
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    searchPlaceholder?: string;
    disabled?: boolean;
    className?: string;
    size?: 'sm' | 'lg';
}

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
    options,
    value,
    onChange,
    placeholder = "Selecione uma opção...",
    searchPlaceholder = "🔍 Buscar...",
    disabled = false,
    className = "",
    size = "lg"
}) => {
    const [showDropdown, setShowDropdown] = useState<boolean>(false);
    const [searchTerm, setSearchTerm] = useState<string>('');

    // Filter options based on search term
    const filteredOptions = options.filter(option =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Get the selected option label
    const selectedOption = options.find(option => option.value === value);

    // Handle clicking outside dropdown to close it
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Element;
            if (!target.closest('.searchable-dropdown-container')) {
                setShowDropdown(false);
                setSearchTerm('');
            }
        };

        if (showDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showDropdown]);

    const handleOptionClick = (optionValue: string) => {
        onChange(optionValue);
        setShowDropdown(false);
        setSearchTerm('');
    };

    return (
        <div className={`position-relative searchable-dropdown-container ${className}`}>
            <Form.Control
                type="text"
                placeholder={placeholder}
                value={selectedOption?.label || ''}
                onClick={() => !disabled && setShowDropdown(!showDropdown)}
                readOnly
                disabled={disabled}
                size={size}
                className={`cursor-pointer ${disabled ? '' : 'cursor-pointer'}`}
                style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
            />

            {showDropdown && !disabled && (
                <div
                    className="position-absolute w-100 bg-white border rounded shadow-sm"
                    style={{
                        zIndex: 1000,
                        maxHeight: '300px',
                        overflowY: 'auto',
                        top: '100%',
                        left: 0
                    }}
                >
                    <div className="p-2 border-bottom">
                        <Form.Control
                            type="text"
                            placeholder={searchPlaceholder}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            size="sm"
                            autoFocus
                        />
                    </div>
                    <div>
                        {filteredOptions.length === 0 ? (
                            <div className="p-3 text-muted text-center">
                                Nenhuma opção encontrada com "{searchTerm}"
                            </div>
                        ) : (
                            filteredOptions.map(option => (
                                <div
                                    key={option.value}
                                    className="p-2 cursor-pointer hover-bg-light"
                                    style={{ cursor: 'pointer' }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                    onClick={() => handleOptionClick(option.value)}
                                >
                                    {option.label}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SearchableDropdown; 